import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import AuthCenterClient from 'auth-center-js-client'
import { AlertType } from 'foundations-library/types'
import { Alert, Button, Typography } from 'foundations-library/components'
import { useAccountSecurityContext, usePhoenixSessionContext } from 'providers'
import { Dialog, LoaderDialogContent, FormButtons } from 'functional-foundations'
import { getMuiTheme } from '../../utils'
import SessionManagement from './../session-management/Index'
import PasswordManagementForm from '../elements/PasswordManagementForm'

const jss = (contextTheme) => {
    return {
        passwordManagement: ({ theme: optionsTheme }) => {
            if (optionsTheme === 'mui') return getMuiTheme()

            if (optionsTheme === 'foundations') return {}
        },
        alert: {
            marginBottom: '20px',
            maxWidth: '660px',
            paddingLeft: '30px',
            margin: '0 auto'
        },
        formContainer: {
            marginBottom: '50px',
            margin: 'auto'
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            alignItems: 'center',
            marginBottom: '50px'
        },
        submitBtn: {
            width: ({ mode }) => mode === 'update' ? '243px' : '100%'
        },
        confirmDialogText: {
            marginBottom: '60px'
        }
    }
}

const useStyles = makeStyles(jss)

type PropsType = {
    mode?: 'update' | 'create',
    openInDialog?: boolean,
    onSave?: (result: 'success' | 'error', err?: any) => any
    logout?: () => any // when used as script tag, we can provide platform where it is used logout callback
    onRedirectToLogin?: () => void,
    theme?: 'foundations' | 'mui' // delete all related mentions as soon as signup updates design
}

const client = new AuthCenterClient()

/**
 *
 */
const App = ({
    mode = 'update',
    openInDialog = false,
    onSave,
    logout,
    onRedirectToLogin,
    theme = 'foundations'
}: PropsType): JSX.Element => {
    const styles = useStyles({ mode, theme })
    const [loading, setLoading] = useState(false)
    const [formIsValid, setFormIsValid] = useState(false)
    const [newPasswordValue, setNewPasswordValue] = useState(null)
    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
    const [formValues, setFormValues] = useState({ current: '', new: '', confirm: '' })
    const [passwordChangeResult, setPasswordChangeResult] = useState<null | AlertType>(null)

    const { session } = usePhoenixSessionContext()

    useEffect(() => {
        if (session.user?.token) {
            client.setSessionToken(session.user.token.split('Bearer ')[1])
        }
    }, [session.user])

    const handlePasswordChangeResult = (level, message = '') => {
        if (!level) setPasswordChangeResult(null)
        setPasswordChangeResult({
            level,
            children: message || (level === 'error' ? 'Something went wrong' : '')
        })
    }

    const { setDevices } = useAccountSecurityContext()

    const triggerOnSaveCallback = (res, err = undefined) => {
        onSave && onSave(res, err)
    }

    const handlePasswordChange = async () => {
        return await client.setPassword({
            password: newPasswordValue,
            currentPassword: formValues.current
        })
    }

    const onSubmit = async (e) => {
        e && e.preventDefault()
        if (openInDialog) return onConfirm()

        try {
            if (mode === 'create') {
                await handlePasswordChange()
                triggerOnSaveCallback('success')
            } else {
                setConfirmDialogIsOpen(true)
            }
        } catch (err) {
            console.error(err)
            handlePasswordChangeResult('error', err?.message)
            triggerOnSaveCallback('error', err)
            throw err
        }
    }

    const onConfirm = async () => {
        setConfirmDialogIsOpen(false)
        setLoading(true)
        try {
            await setDevices()
            // fetch devices first to handle any device fetch errors before updating password
            // & avoid showing the loading dialog for fetching devices
            await handlePasswordChange()
            handlePasswordChangeResult('success')
        } catch (err) {
            console.error(err)
            handlePasswordChangeResult('error', err?.message)
        } finally {
            setLoading(false)
        }
    }

    const onForgotPasswordClick = () => {
        alert('Forgot new password demo')
    }

    const renderForm = () => {
        const form = (<form onSubmit={onSubmit}>
            {passwordChangeResult && <Alert className={styles.alert} {...passwordChangeResult} />}
            <PasswordManagementForm
                isValid={setFormIsValid}
                onChange={setNewPasswordValue}
                onChangeValues={setFormValues}
                mode={mode}
                className={styles.formContainer}
            />
            <div className={styles.buttonsContainer}>
                {mode === 'update' && <Button
                    onClick={onForgotPasswordClick}
                    size='large'
                    variant='text'
                    icon='go'
                    type='button'
                >Forgot password</Button>}
                <Button
                    size='large'
                    icon='go'
                    className={`${styles.submitBtn} submit-button`}
                    type='submit'
                    disabled={!formIsValid}
                >{mode === 'create' ? 'Create' : 'Change'} password</Button>
            </div>
        </form>)

        let formComponent = form

        if (openInDialog) {
            formComponent = (
                <Dialog isOpen={passwordChangeResult?.level !== 'success' && !loading}>
                    <Typography variant='h6' align='center' style={{ marginBottom: '50px' }}>Create a new password</Typography>
                    {form}
                </Dialog>
            )
        }

        return formComponent
    }

    return (
        <div className={`${styles.passwordManagement} password-management`}>
            {renderForm()}
            <Dialog isOpen={confirmDialogIsOpen}>
                <Typography
                    variant='h4'
                    align='center'
                    className={styles.confirmDialogText}
                >Are you sure you want to change your password?</Typography>
                <FormButtons
                    cancelText='Go back'
                    confirmText='Yes, change my password'
                    cancelProps={{ color: 'neutral', variant: 'outlined' }}
                    onCancel={() => setConfirmDialogIsOpen(false)}
                    onConfirm={onConfirm}
                />
            </Dialog>
            {loading && <Dialog isOpen={true}>
                <LoaderDialogContent
                    title='Updating your password...'
                    subtitle='Please wait!'
                />
            </Dialog>}
            {
                passwordChangeResult?.level === 'success' &&
                <SessionManagement hasPasswordUpdated={true} logout={logout} onRedirectToLogin={onRedirectToLogin} />
            }
        </div>
    )
}

export default App
